import React, { useState } from 'react';
import { API_URL } from '../config';
import { pdf } from '@react-pdf/renderer';
import { toast } from 'react-toastify';
import { FaFilePdf, FaEnvelope, FaSpinner } from 'react-icons/fa';
import QuotePDF from './QuotePDF';
import { getCompanyDetails } from '../config/CompanyDetails';

const formatCurrency = (amount) => {
  if (!amount) return 'R 0.00';
  return `R ${parseFloat(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

// Ensures a proforma exists for the job (returns existing one if already created).
const ensureProforma = async (jobId) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_URL}/invoices/proforma/from-job/${jobId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });
  const data = await res.json();
  if (!data.success) {
    throw new Error(data.error || 'Failed to create proforma invoice');
  }
  return data;
};

function ProformaInvoiceButton({ job, onSuccess }) {
  const [busy, setBusy] = useState(false);

  // ── Generate Proforma (download PDF) ──
  const handleGenerate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const data = await ensureProforma(job.id);
      const quote = data.quote;
      const jobRecord = data.job;
      const invoice = data.invoice;

      if (!quote) {
        toast.error('This job has no linked quote, so the PDF cannot be generated from quote data. The proforma is still saved in Invoices.');
        if (onSuccess) onSuccess();
        return;
      }

      const proformaMeta = {
        invoice_number: invoice.invoice_number,
        date: invoice.invoice_date,
        job_number: jobRecord?.job_number || job.job_number,
        pickup_date: jobRecord?.start_date || job.start_date || null
      };

      const company = await getCompanyDetails();
      const blob = await pdf(<QuotePDF quote={quote} variant="proforma" proforma={proformaMeta} companyDetails={company} credit={data.available_credit || null} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Proforma_${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      toast.success(`✅ Proforma ${invoice.invoice_number} downloaded!`);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Proforma generate error:', err);
      toast.error(err.message || 'Failed to generate proforma.');
    } finally {
      setBusy(false);
    }
  };

  // ── Send Proforma (opens pre-filled email, mirrors Send Quote) ──
  const handleSend = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const data = await ensureProforma(job.id);
      const quote = data.quote;
      const jobRecord = data.job;
      const invoice = data.invoice;

      const customerName = quote?.customer_name || job?.client?.company_name || 'Customer';
      const contactEmail = quote?.contact_email || job?.client?.contact_email || '';

      // Reflect any client credit on the proforma in the email body too.
      const creditInfo = (data.available_credit && data.available_credit.total > 0) ? data.available_credit : null;
      const creditTotal = creditInfo ? Math.min(Number(creditInfo.total) || 0, Number(invoice.total_amount) || 0) : 0;
      const creditRefs = creditInfo ? (creditInfo.notes || []).map((n) => n.number).filter(Boolean).join(', ') : '';
      const amountDue = Math.max((Number(invoice.total_amount) || 0) - creditTotal, 0);

      const subject = `Proforma Invoice ${invoice.invoice_number} — Job ${jobRecord?.job_number || job.job_number || ''}`;
      const body = [
        `Dear ${customerName},`,
        '',
        `Please find attached our proforma invoice ${invoice.invoice_number} for job ${jobRecord?.job_number || job.job_number || ''}.`,
        '',
        `Proforma: ${invoice.invoice_number}`,
        `Job: ${jobRecord?.job_number || job.job_number || ''}`,
        `Total: ${formatCurrency(invoice.total_amount)}`,
        ...(creditTotal > 0 ? [
          `Less credit${creditRefs ? ` (${creditRefs})` : ''}: -${formatCurrency(creditTotal)}`,
          `Amount due: ${formatCurrency(amountDue)}`
        ] : []),
        '',
        'Kind regards,',
        'One Purpose Logistics'
      ].join('\n');

      window.location.href = `mailto:${contactEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      toast.info('Email client opened. Attach the Proforma PDF manually.');
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Proforma send error:', err);
      toast.error(err.message || 'Failed to prepare proforma email.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex gap-2">
      <button
        onClick={handleGenerate}
        disabled={busy}
        className="flex items-center gap-2 px-3 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg text-xs font-medium disabled:opacity-50"
      >
        {busy ? <FaSpinner className="animate-spin" size={12} /> : <FaFilePdf size={12} />}
        Generate Proforma
      </button>
      <button
        onClick={handleSend}
        disabled={busy}
        className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium disabled:opacity-50"
      >
        {busy ? <FaSpinner className="animate-spin" size={12} /> : <FaEnvelope size={12} />}
        Send Proforma
      </button>
    </div>
  );
}

export default ProformaInvoiceButton;